import {
  Query,
  BookingType,
  PricingMode,
  TimeOnly,
  GetMarketplaceAvailableListingQuery,
  GetMarketplaceAvailableCalendarPricingQuery,
} from "@repo/rq-api-public";
import { useAtomValue } from "jotai";
import {
  listingGuidAtom,
  campaignCodeAtom,
  campaignValidationAtom,
  passengersAtom,
  selectedPackageAtom,
  selectedSeasonAtom,
  selectedDateAtom,
  currentStepAtom,
} from "../components/widget/store";
import { combineDateAndTime, formatTimeForApi } from "../utils/date";

export const useListingQuery = () => {
  const listingGuid = useAtomValue(listingGuidAtom);
  const query = Query.useListingsGuidGETQuery(listingGuid!, {
    enabled: !!listingGuid,
  });
  return query;
};

/**
 * Request Only listings cannot take payment while the booking is an Enquiry,
 * so the widget sends an enquiry instead of continuing to the payment step.
 */
export const useIsRequestOnlyListing = () => {
  const { data: listing } = useListingQuery();
  return listing?.bookingType === BookingType.RequestOnly;
};

const calculateEndDateTime = (
  startDate: Date,
  startTime?: TimeOnly,
  duration?: { hours?: number; minutes?: number },
) => {
  if (!startTime || !duration) {
    return { endDate: startDate, endTime: undefined };
  }

  const startDateTime = new Date(startDate);
  startDateTime.setHours(startTime.hour, startTime.minute, 0, 0);

  const durationMs =
    (duration.hours || 0) * 60 * 60 * 1000 +
    (duration.minutes || 0) * 60 * 1000;
  const endDateTime = new Date(startDateTime.getTime() + durationMs);

  return {
    endDate: endDateTime,
    endTime: new TimeOnly({
      hour: endDateTime.getHours(),
      minute: endDateTime.getMinutes(),
    }),
  };
};

export const usePricingCalculateQuery = (options?: {
  promoCode?: string;
  promoCodeStartDateTime?: Date;
  enabled?: boolean;
}) => {
  const selectedPackage = useAtomValue(selectedPackageAtom);
  const selectedSeason = useAtomValue(selectedSeasonAtom);
  const passengers = useAtomValue(passengersAtom);
  const campaignCode = useAtomValue(campaignCodeAtom);
  const campaignValidation = useAtomValue(campaignValidationAtom);
  const selectedDate = useAtomValue(selectedDateAtom);
  const currentStep = useAtomValue(currentStepAtom);
  const listingGuid = useAtomValue(listingGuidAtom);
  const { data: listing } = useListingQuery();

  // Campaign codes and promo codes are mutually exclusive
  // Only use campaign if it's present AND valid AND we're in step 3 or later
  const shouldUseCampaign =
    !!campaignCode && campaignValidation.isValid === true && currentStep >= 3;
  const shouldUsePromo = !shouldUseCampaign && !!options?.promoCode;

  const isCalendarMode = listing?.pricingMode === PricingMode.Calendar;

  // For calendar mode, use full day (00:00 to 23:59)
  // For package mode, calculate from package times
  const { endDate, endTime } = isCalendarMode
    ? {
        endDate: selectedDate,
        endTime: new TimeOnly({ hour: 23, minute: 59 }),
      }
    : calculateEndDateTime(
        selectedDate,
        selectedPackage?.startTime,
        selectedPackage?.duration,
      );

  const packageQuery = Query.usePricingsCalculateGETQuery(
    {
      pricingGuid: selectedPackage?.pricingGuid || undefined,
      passengers: passengers ?? undefined,
      // Only send promo code params if no valid campaign code is present
      promoCodeRequest_PromoCode: shouldUsePromo
        ? options?.promoCode
        : undefined,
      promoCodeRequest_StartDateTime: shouldUsePromo
        ? options?.promoCodeStartDateTime
        : undefined,
      // Only send campaign code params if campaign code is valid AND we're in step 3+
      campaignCodeRequest_Code: shouldUseCampaign
        ? (campaignCode ?? undefined)
        : undefined,
      campaignCodeRequest_StartDateTime: shouldUseCampaign
        ? selectedDate
        : undefined,
      startDateTime: combineDateAndTime(
        selectedDate,
        selectedPackage?.startTime,
      ),
    },
    {
      enabled:
        !isCalendarMode &&
        !!selectedPackage?.pricingGuid &&
        (options?.enabled ?? true),
    },
  );

  const calendarQuery = Query.usePricingsCalculateCalendarGETQuery(
    {
      listingGuid: listingGuid || undefined,
      startDate: selectedDate,
      // oxlint-disable-next-line typescript/no-explicit-any criminal
      startTime: "00:00" as any,
      endDate,
      // oxlint-disable-next-line typescript/no-explicit-any criminal
      endTime: formatTimeForApi(endTime) as any,
      passengers: passengers ?? undefined,
      promoCode: shouldUsePromo ? options?.promoCode : undefined,
      promoCodeStartDateTime: shouldUsePromo
        ? options?.promoCodeStartDateTime
        : undefined,
      campaignCode: shouldUseCampaign ? (campaignCode ?? undefined) : undefined,
      campaignCodeStartDateTime: shouldUseCampaign ? selectedDate : undefined,
    },
    {
      enabled:
        isCalendarMode &&
        !!listingGuid &&
        !!selectedSeason &&
        (options?.enabled ?? true),
    },
  );

  return isCalendarMode ? calendarQuery : packageQuery;
};

export const useAvailabilityCheck = () => {
  const { data: listing } = useListingQuery();
  const isCalendarMode = listing?.pricingMode === PricingMode.Calendar;

  const { mutateAsync: listingAvailableMutate } =
    Query.useListingsAvailablePOSTMutation();
  const { mutateAsync: calendarAvailableMutate } =
    Query.useListingsCalendarAvailablePOSTMutation();

  const checkAvailability = async (params: {
    listingGuid: string;
    selectedDate: Date;
    selectedPackage?: { pricingGuid?: string; startTime?: TimeOnly } | null;
  }): Promise<{ isAvailable?: boolean } | undefined> => {
    if (isCalendarMode) {
      return await calendarAvailableMutate(
        new GetMarketplaceAvailableCalendarPricingQuery({
          listingGuid: params.listingGuid,
          startDate: params.selectedDate,
          endDate: params.selectedDate,
        }),
      );
    } else {
      return await listingAvailableMutate(
        new GetMarketplaceAvailableListingQuery({
          pricingGuid: params.selectedPackage?.pricingGuid,
          startDate: params.selectedDate,
          startTime: params.selectedPackage?.startTime,
        }),
      );
    }
  };

  return { checkAvailability, isCalendarMode };
};
